/**
 * Market Restriction Validation
 * ==============================
 * Validates cart items against market-based country restrictions.
 * - Reads restriction data from the Liquid-generated JSON block
 * - Shows per-item warnings for restricted products
 * - Shows a global warning above checkout actions
 * - Disables checkout button when any item is restricted
 * - Handles AJAX removal of restricted items
 * - Re-validates after every cart update (section re-render)
 */

(function () {
  'use strict';

  const SELECTORS = {
    dataScript: '.CartMarketRestrictionData',
    // Per-item warnings (both cart drawer and cart page use the same data attribute)
    itemWarning: '[data-market-warning-key]',
    removeBtn: '[data-market-remove-key]',
    // Global warnings
    drawerGlobalWarning: '#CartDrawer-MarketGlobalWarning',
    pageGlobalWarning: '#Cart-MarketGlobalWarning',
    // Checkout buttons (both drawer and page)
    checkoutBtns: '[data-market-checkout]',
    // Cart item rows
    drawerItem: '[id^="CartDrawer-Item-"]',
    pageItem: '[id^="CartItem-"]',
    // Dynamic checkout buttons
    dynamicCheckout: '.cart__dynamic-checkout-buttons',
  };

  /**
   * Parse the restriction data JSON from the Liquid snippet.
   * Returns null if not found (e.g. empty cart or snippet not rendered).
   */
  function getRestrictionData() {
    const el = document.querySelector(SELECTORS.dataScript);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[MarketRestriction] Failed to parse restriction data:', e);
      return null;
    }
  }

  /**
   * Main validation function.
   * Loops through items, toggles per-item warnings,
   * toggles global warning, and enables/disables checkout.
   */
  function validateCart() {
    const data = getRestrictionData();
    if (!data || !data.items) return;

    let hasRestricted = false;

    data.items.forEach(function (item) {
      const isRestricted = item.isRestricted === true;
      if (isRestricted) hasRestricted = true;

      // Toggle per-item warnings (may exist in drawer, page, or both)
      const warnings = document.querySelectorAll(
        '[data-market-warning-key="' + item.key + '"]'
      );
      warnings.forEach(function (warning) {
        warning.style.display = isRestricted ? '' : 'none';
      });

      // Add/remove visual class on the cart item row
      const drawerRow = document.getElementById('CartDrawer-Item-' + item.index);
      const pageRow = document.getElementById('CartItem-' + item.index);
      [drawerRow, pageRow].forEach(function (row) {
        if (!row) return;
        if (isRestricted) {
          row.classList.add('cart-item--market-restricted');
        } else {
          row.classList.remove('cart-item--market-restricted');
        }
      });
    });

    // Toggle global warnings
    toggleGlobalWarning(hasRestricted);
    // Toggle checkout buttons
    toggleCheckout(hasRestricted);
  }

  /**
   * Show/hide the global warning banners.
   */
  function toggleGlobalWarning(show) {
    [SELECTORS.drawerGlobalWarning, SELECTORS.pageGlobalWarning].forEach(function (sel) {
      const el = document.querySelector(sel);
      if (el) el.style.display = show ? '' : 'none';
    });
  }

  /**
   * Enable/disable checkout buttons.
   */
  function toggleCheckout(disable) {
    document.querySelectorAll(SELECTORS.checkoutBtns).forEach(function (btn) {
      if (disable) {
        btn.disabled = true;
        btn.classList.add('cart__checkout-button--disabled');
      } else {
        // Only re-enable if cart is not empty (don't override empty-cart disabled state)
        const cartDrawer = document.querySelector('cart-drawer');
        const isEmpty = cartDrawer && cartDrawer.classList.contains('is-empty');
        if (!isEmpty) {
          btn.disabled = false;
        }
        btn.classList.remove('cart__checkout-button--disabled');
      }
    });

    // Also disable dynamic checkout buttons (Apple Pay, etc.)
    document.querySelectorAll(SELECTORS.dynamicCheckout).forEach(function (el) {
      if (disable) {
        el.classList.add('cart__dynamic-checkout--disabled');
      } else {
        el.classList.remove('cart__dynamic-checkout--disabled');
      }
    });
  }

  /**
   * Handle AJAX removal of a restricted item.
   * Uses Shopify's /cart/change.js endpoint then refreshes sections.
   */
  function handleRemove(event) {
    event.preventDefault();
    const btn = event.target.closest('[data-market-remove-key]');
    if (!btn) return;

    const itemKey = btn.getAttribute('data-market-remove-key');
    const itemIndex = parseInt(btn.getAttribute('data-market-remove-index'), 10);

    btn.disabled = true;
    btn.textContent = 'Removing…';

    // Use the cart-items or cart-drawer-items custom element if available
    // to leverage their built-in section rendering. Otherwise fall back to fetch.
    const cartItemsEl =
      btn.closest('cart-drawer-items') || btn.closest('cart-items');

    if (cartItemsEl && typeof cartItemsEl.updateQuantity === 'function') {
      // Use the theme's built-in updateQuantity to keep everything in sync
      cartItemsEl.updateQuantity(itemIndex, 0, {
        preventDefault: function () {},
        currentTarget: btn,
        target: btn,
      });
      return;
    }

    // Fallback: manual AJAX removal
    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: itemKey,
        quantity: 0,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        // Refresh the page sections
        refreshCartSections();
      })
      .catch(function (err) {
        console.error('[MarketRestriction] Remove failed:', err);
        btn.disabled = false;
        btn.textContent = 'Remove';
      });
  }

  /**
   * Refresh both cart drawer and cart page sections after manual AJAX removal.
   */
  function refreshCartSections() {
    // Refresh cart drawer
    var drawerPromise = fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        if (sections['cart-drawer']) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          var newDrawer = doc.querySelector('#CartDrawer');
          var currentDrawer = document.getElementById('CartDrawer');
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
          }
          // Re-attach and re-validate
          attachRemoveListeners();
          reParseAndValidate();
        }
        if (sections['cart-icon-bubble']) {
          var bubbleEl = document.getElementById('cart-icon-bubble');
          if (bubbleEl) {
            var parser2 = new DOMParser();
            var doc2 = parser2.parseFromString(sections['cart-icon-bubble'], 'text/html');
            var newBubble = doc2.querySelector('.shopify-section');
            if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
          }
        }
      })
      .catch(function (err) {
        console.error('[MarketRestriction] Drawer refresh failed:', err);
      });

    // Refresh cart page sections if they exist
    var mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems) {
      var sectionId = mainCartItems.getAttribute('data-id');
      fetch('/?sections=' + sectionId)
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          if (sections[sectionId]) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(sections[sectionId], 'text/html');
            var newContent = doc.querySelector('.js-contents');
            var currentContent = mainCartItems.querySelector('.js-contents');
            if (newContent && currentContent) {
              currentContent.innerHTML = newContent.innerHTML;
              attachRemoveListeners();
              reParseAndValidate();
            }
          }
        })
        .catch(function (err) {
          console.error('[MarketRestriction] Page refresh failed:', err);
        });
    }
  }

  /**
   * After a section re-render, the JSON data block is refreshed too.
   * Re-parse and re-validate.
   */
  function reParseAndValidate() {
    // Small delay to ensure DOM is fully updated
    requestAnimationFrame(function () {
      validateCart();
    });
  }

  /**
   * Attach click listeners to all remove buttons.
   */
  function attachRemoveListeners() {
    document.querySelectorAll(SELECTORS.removeBtn).forEach(function (btn) {
      // Prevent duplicate listeners
      if (btn._marketRemoveAttached) return;
      btn._marketRemoveAttached = true;
      btn.addEventListener('click', handleRemove);
    });
  }

  /**
   * Observe the cart drawer and cart page for section re-renders.
   * When the theme's JS replaces inner HTML (after quantity changes, etc.),
   * we need to re-attach listeners and re-validate.
   */
  function observeCartChanges() {
    var targets = [
      document.getElementById('CartDrawer'),
      document.getElementById('main-cart-items'),
      document.getElementById('main-cart-footer'),
    ].filter(Boolean);

    if (!targets.length) return;

    var observer = new MutationObserver(function (mutations) {
      // Debounce: only run once per batch of mutations
      clearTimeout(observer._timeout);
      observer._timeout = setTimeout(function () {
        attachRemoveListeners();
        validateCart();
      }, 100);
    });

    targets.forEach(function (target) {
      observer.observe(target, { childList: true, subtree: true });
    });
  }

  /**
   * Subscribe to Shopify's PUB_SUB cartUpdate event if available.
   * This fires after the theme's cart.js updates sections.
   */
  function subscribeToCartUpdates() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        // After section re-render, re-validate
        setTimeout(function () {
          attachRemoveListeners();
          validateCart();
        }, 300);
      });
    }
  }

  /**
   * Initialize everything.
   */
  function init() {
    attachRemoveListeners();
    validateCart();
    observeCartChanges();
    subscribeToCartUpdates();
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
